/**
 * Typed API error classes for structured error handling across the app.
 *
 * - RateLimitError  → HTTP 429 with optional Retry-After
 * - ApiError        → any non-2xx response
 * - NetworkError    → fetch failure (offline, DNS, CORS, etc.)
 *
 * The `showRateLimitToast` helper displays a sonner toast once per throttle
 * window so that parallel 429 responses do not spam the user.
 */

import { toast } from "sonner";
import i18n from "@/i18n";
import { captureStorefrontError } from "@/lib/error-capture";

// ---------------------------------------------------------------------------
// Error classes
// ---------------------------------------------------------------------------

export class RateLimitError extends Error {
  readonly retryAfter: number; // seconds

  constructor(retryAfter: number) {
    super("Rate limited");
    this.name = "RateLimitError";
    this.retryAfter = retryAfter;
  }
}

/** Field-level validation error as returned by Zod-backed admin API routes. */
export interface ApiErrorDetail {
  readonly field: string;
  readonly message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly details?: readonly ApiErrorDetail[];

  constructor(
    status: number,
    message: string,
    code?: string,
    details?: readonly ApiErrorDetail[],
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /**
   * Build a human-readable summary combining the top-level message and any
   * field-level details. Falls back to the plain message when there are no
   * details. Suitable for toast display.
   */
  toHumanMessage(): string {
    if (!this.details || this.details.length === 0) return this.message;
    const fields = this.details
      .map((d) => `${d.field}: ${d.message}`)
      .join(" · ");
    return `${this.message} — ${fields}`;
  }
}

export class NetworkError extends Error {
  constructor(message = "Network error") {
    super(message);
    this.name = "NetworkError";
  }
}

// ---------------------------------------------------------------------------
// Toast helpers — deduplicated so parallel failures only show one toast
// ---------------------------------------------------------------------------

let lastRateLimitToastAt = 0;
const RATE_LIMIT_TOAST_COOLDOWN_MS = 3_000;

export function showRateLimitToast(retryAfter: number): void {
  const now = Date.now();
  if (now - lastRateLimitToastAt < RATE_LIMIT_TOAST_COOLDOWN_MS) return;
  lastRateLimitToastAt = now;

  const t = i18n.t.bind(i18n);
  toast.warning(t("errors.rateLimited", { seconds: retryAfter }), {
    duration: Math.max(retryAfter * 1_000, 4_000),
    id: "rate-limit", // prevents stacking
  });
}

export function showServerErrorToast(): void {
  toast.error(i18n.t("errors.serverError"), { id: "server-error" });
}

export function showNetworkErrorToast(): void {
  toast.error(i18n.t("errors.networkError"), { id: "network-error" });
}

// Codes the backend sends back for stale/missing MFA on Direction admins.
// Surfaced as a single deduped toast instead of the generic server-error
// toast so the user gets actionable guidance on every permission-gated page.
const MFA_ERROR_CODES: ReadonlySet<string> = new Set(["MFA_REQUIRED", "MFA_STALE"]);

export function isMfaError(code: string | undefined): boolean {
  return code !== undefined && MFA_ERROR_CODES.has(code);
}

export function showMfaRequiredToast(): void {
  toast.warning(
    i18n.t(
      "errors.mfaRequired",
      "Votre vérification MFA a expiré. Veuillez vous reconnecter pour continuer.",
    ),
    { id: "mfa-required", duration: 10_000 },
  );
}

// ---------------------------------------------------------------------------
// Query-client level error handler (called from React Query onError)
// ---------------------------------------------------------------------------

/**
 * Queries can opt out of the global error toast by setting `meta.silent: true`.
 * Use this for non-critical data (CMS sections, social proof, etc.) where
 * failing silently is better than spamming the user with toasts.
 *
 * All errors — silent or not — are forwarded to the error capture service
 * so admins can see them in the Error Dashboard.
 */
export function handleQueryError(
  error: unknown,
  query?: { meta?: Record<string, unknown> },
): void {
  // Always capture for admin visibility, regardless of silent flag
  captureStorefrontError(error);

  // If the query opted into silent mode, skip the toast entirely
  const silent = query?.meta?.silent === true;

  // RateLimitError toasts are already shown by apiFetch — skip to avoid doubles
  if (error instanceof RateLimitError) return;

  if (error instanceof ApiError) {
    // 401 is handled by apiFetch (redirect) — skip
    if (error.status === 401) return;
    // 403 MFA_REQUIRED / MFA_STALE: a Direction admin with stale or missing
    // MFA fires this on every permission-gated endpoint, which meant every
    // page opened a new "Une erreur est survenue" toast. Show one actionable
    // toast instead (sonner dedupes via `id`).
    if (error.status === 403 && isMfaError(error.code)) {
      if (!silent) showMfaRequiredToast();
      return;
    }
    if (!silent) showServerErrorToast();
    return;
  }

  if (error instanceof NetworkError) {
    if (!silent) showNetworkErrorToast();
    return;
  }

  // Unknown errors — capture but don't toast if silent
  if (!silent) showServerErrorToast();
}
